import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, List, LayoutGrid, ArrowRight } from "lucide-react";
import { fetchPublicLots, fetchPublicMeta, mediaSrc, b2bBase } from "@/b2b/b2bApi";
import {
  CATEGORY_ORDER, CATEGORY_LABELS, CATEGORY_ICON, STATUS_CONFIG, fmtMoney,
} from "@/b2b/constants";

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow, noarchive";
    document.head.appendChild(m);
    const prevTitle = document.title;
    document.title = "RentOPC B2B — Dealer & Reseller Stock";
    return () => { document.head.removeChild(m); document.title = prevTitle; };
  }, []);
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.AVAILABLE;
  return (
    <span data-testid={`lot-status-${status}`}
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function B2BCataloguePage() {
  useNoIndex();
  const base = b2bBase();
  const [meta, setMeta] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [view, setView] = useState("list"); // list | picture

  useEffect(() => { fetchPublicMeta().then(setMeta).catch(() => {}); }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(() => {
      fetchPublicLots({ q: q.trim() || undefined, category: cat })
        .then((d) => { if (!cancel) setLots(d); })
        .catch(() => { if (!cancel) setLots([]); })
        .finally(() => setLoading(false));
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, cat]);

  const lastUpdated = useMemo(() => {
    if (!meta?.last_updated) return null;
    return new Date(meta.last_updated).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  }, [meta]);

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-[#17202a]" data-testid="b2b-catalogue">
      {/* Header — no admin link, per brief */}
      <header className="sticky top-0 z-10 bg-[#111827] text-white shadow-[0_2px_12px_#0002]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-xl font-extrabold tracking-tight">RENTOPC <span className="text-emerald-400">B2B</span></div>
            <div className="text-[11px] text-slate-300">Dealer &amp; Reseller Stock Catalogue</div>
          </div>
          {lastUpdated && (
            <div className="text-[11px] text-slate-400 text-right" data-testid="last-updated">
              Last updated<br /><span className="text-slate-200">{lastUpdated}</span>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        {/* Controls */}
        <div className="mb-5">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative flex-1 min-w-0">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="b2b-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search lot, model, brand, capacity…"
                className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-slate-800"
              />
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button data-testid="view-list" onClick={() => setView("list")}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm ${view === "list" ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-slate-200 text-slate-700"}`}>
                <List size={15} /> List
              </button>
              <button data-testid="view-picture" onClick={() => setView("picture")}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm ${view === "picture" ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-slate-200 text-slate-700"}`}>
                <LayoutGrid size={15} /> Picture
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="category-chips">
            {CATEGORY_ORDER.map((c) => {
              const Icon = CATEGORY_ICON[c];
              const active = cat === c;
              return (
                <button key={c} data-testid={`chip-${c}`} onClick={() => setCat(c)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm ${active ? "bg-[#111827] text-white border-[#111827]" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                  <Icon size={14} /> {CATEGORY_LABELS[c]}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400 text-sm">Loading stock…</div>
        ) : lots.length === 0 ? (
          <div className="py-20 text-center text-slate-500 text-sm" data-testid="b2b-empty">
            No matching lots found.
          </div>
        ) : view === "list" ? (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="b2b-list">
            <div className="hidden sm:grid grid-cols-[110px_1fr_90px_120px_120px] gap-2 bg-slate-50 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <div>Lot</div><div>Stock</div><div className="text-right">Qty</div><div className="text-right">From</div><div className="text-right">Status</div>
            </div>
            {lots.map((l) => (
              <Link key={l.id} to={`${base}/lot/${encodeURIComponent(l.lot_number)}`}
                data-testid={`lot-row-${l.lot_number}`}
                className="grid grid-cols-1 sm:grid-cols-[110px_1fr_90px_120px_120px] gap-1 sm:gap-2 px-4 py-3.5 border-t border-slate-100 hover:bg-slate-50 transition-colors">
                <div>
                  <div className="font-extrabold text-[13px]">{l.lot_number}</div>
                  <div className="text-xs text-slate-500">{CATEGORY_LABELS[l.category] || l.category}</div>
                </div>
                <div className="min-w-0">
                  <strong className="block text-sm">{l.lot_name}</strong>
                  <span className="block text-xs text-slate-500">
                    {l.item_count} line item{l.item_count === 1 ? "" : "s"}{l.brands_summary ? ` · ${l.brands_summary}` : ""}
                  </span>
                </div>
                <div className="sm:text-right text-[13px] font-bold">
                  <span className="sm:hidden text-slate-400 font-normal">Qty: </span>{l.total_available_quantity}/{l.total_original_quantity}
                </div>
                <div className="sm:text-right text-[13px] font-bold whitespace-nowrap">{fmtMoney(l.price_from) || "—"}</div>
                <div className="sm:text-right"><StatusBadge status={l.status} /></div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(245px,1fr))]" data-testid="b2b-cards">
            {lots.map((l) => (
              <Link key={l.id} to={`${base}/lot/${encodeURIComponent(l.lot_number)}`}
                data-testid={`lot-card-${l.lot_number}`}
                className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-[175px] bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  {l.primary_image_url ? (
                    <img src={mediaSrc(l.primary_image_url)} alt={l.lot_name} loading="lazy"
                      className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-slate-400 text-xs">No photo</span>
                  )}
                </div>
                <div className="p-3.5">
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <h3 className="text-sm font-semibold leading-snug">{l.lot_name}</h3>
                    <StatusBadge status={l.status} />
                  </div>
                  <div className="text-xs text-slate-500 leading-relaxed">
                    {l.lot_number} · {CATEGORY_LABELS[l.category]}<br />
                    {l.item_count} line item{l.item_count === 1 ? "" : "s"}{l.brands_summary ? ` · ${l.brands_summary}` : ""}
                  </div>
                  <div className="mt-2.5 flex items-center justify-between">
                    <div className="text-[15px] font-extrabold">{fmtMoney(l.price_from) || "—"}<span className="text-xs font-normal text-slate-400"> from</span></div>
                    <span className="text-xs text-slate-500">{l.total_available_quantity} avail</span>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-700">View Lot <ArrowRight size={13} /></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
